import { html } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import resetStyle from '../../shared/components/html-content/reset.style';
import messageInstructorViewStyle from './message-instructor.style';
import { ludsBaseFont } from '../../shared/styles/typography';
import '../../design-system/modal/modal';
import '../../design-system/button/button';
import { MessageInstructorClient } from './message-instructor-client';
import { MessageInstructorModel, MessageData, ResponseType } from './message-instructor.model';
import { i18n } from '../../i18n/i18n';
import { LuaModalElement } from '../../shared/components/lua-modal/lua-modal';

const ALLOWED_PATTERN = /[^a-zA-Z0-9 .,!?@#&()\-_'":;\n]/g;

@customElement('lua-message-instructor')
export class LuaMessageInstructor extends LuaModalElement {
  /**
   * The enrollment ID associated with the message instructor dialog.
   * Used to identify the current user's enrollment when sending a message to the instructor.
   */
  @property({ type: Number, reflect: true })
  enrollmentId: number = null;

  /**
   * Instance of MessageInstructorClient used to send messages to the instructor service.
   * Handles API communication for message submission and error handling.
   */
  private messageInstructorClient: MessageInstructorClient = new MessageInstructorClient();

  @state() private message: MessageData = {
    subject: '',
    body: '',
  };

  static override styles = [resetStyle, ludsBaseFont, messageInstructorViewStyle];

  /**
   * Closes the message instructor dialog or view.
   * Resets the form and emits a 'close-instructor' event to notify parent components.
   */
  private closeMessageInstructor() {
    this.message = {
      subject: '',
      body: '',
    };
    this.dispatchEvent(
      new CustomEvent('close-instructor', {
        detail: false,
        bubbles: true,
        composed: true,
      })
    );
  }

  /**
   * Handles the form submission event for sending a message to the instructor.
   * Collects form data, validates input, constructs the message object, and calls the API client.
   * On success, closes the dialog.
   *
   */
  private async handleSubmit() {
    const messageInstruction: MessageInstructorModel = {
      enrollment_id: this.enrollmentId,
      message: this.message,
      message_to_author: true,
      disable_redirect: true,
    };
    const res = await this.messageInstructorClient.sendMessageToInstructor(messageInstruction);
    if (res === ResponseType.SUCCESS) {
      this.showModal(false);
    }
  }

  /**
   * Restricts input to alphanumeric characters and a set of safe symbols for both input and textarea fields.
   *
   * @param event - The input event from the field
   */
  private _onInput(event: Event) {
    const element = event.target as HTMLInputElement | HTMLTextAreaElement;
    element.value = element.value.replace(ALLOWED_PATTERN, '');
    this.message[element.name as keyof MessageData] = element.value;
    this.requestUpdate();
  }

  override render() {
    const isDisabled = !this.message.body || !this.message.subject;
    return html`
      <luds-modal
        .open=${this.open}
        title="${i18n('lua.message_instructor.message_to_instructor')}"
        width="sm"
        id="message-instructor-modal"
        @luds-dialog-close="${this.closeMessageInstructor}">
        <form id="message-instructor-form" class="message-instructor">
          <div class="field">
            <label for="subject-input">${i18n('lua.message_instructor.subject')}</label>
            <input
              id="subject-input"
              @input=${this._onInput}
              .value=${this.message.subject}
              type="text"
              name="subject"
              class="lua-input"
              placeholder="${i18n('lua.message_instructor.title')}"
              required />
          </div>
          <div class="field">
            <label for="body-input">${i18n('lua.message_instructor.message')}</label>
            <textarea
              id="body-input"
              @input=${this._onInput}
              .value=${this.message.body}
              name="body"
              rows="4"
              class="lua-textarea"
              placeholder="${i18n('lua.message_instructor.message_placeholder')}"
              required></textarea>
          </div>
        </form>
        <div slot="footer">
          <div class="footer-buttons">
            <luds-button @click="${() => this.showModal(false)}">${i18n('lua.message_instructor.cancel')}</luds-button>
            <luds-button @click="${this.handleSubmit}" ?disabled="${isDisabled}"
              >${i18n('lua.message_instructor.ok')}</luds-button
            >
          </div>
        </div>
      </luds-modal>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'lua-message-instructor': LuaMessageInstructor;
  }
}
